// vpn-web-bo/src/pages/accounting/grants.tsx
//
// VPN-75 兌換／發放紀錄頁：日期 / 兌換碼 / 裝置 / 用戶 / 狀態 篩選 + 表格 + CSV。
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { TableProps } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router";
import { authFetch } from "../../utils/api";
import { BrandScopeNotice } from "../../components/BrandScopeNotice";
import { getBrandLabel, tokenManager } from "../../utils/tokenManager";
import { useViewScope } from "../../utils/viewScope";
import {
  formatMb,
  downloadAccountingCsv,
  unwrap,
  describeGrantStatus,
  type AccountingGrantRow,
  type AccountingGrantsResponse,
} from "./lib";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const API_PATH = "/api/admin/accounting/grants";

// VPN-75 fix（Wave-0）：對齊 DB `vpn_status` enum（active / expired / depleted）。
// 舊 'exhausted' 改用 DB 真值 'depleted'（label 維持「已用罄」）；移除 'disabled'
// （DB 無此狀態，原本送下去 mapStatusFilter 原樣傳 → 永遠查不到任何資料的幽靈選項）。
const STATUS_OPTIONS = [
  { label: "有效", value: "active" },
  { label: "已過期", value: "expired" },
  { label: "已用罄", value: "depleted" },
];
const SOURCE_OPTIONS = [
  { label: "全部帳本", value: "all" },
  { label: "舊版發放", value: "legacy_grant" },
  { label: "會員權益", value: "member_entitlement" },
];

export const AccountingGrantsPage: React.FC = () => {
  const isAllBrand = tokenManager.isAllBrandAdmin();

  const [data, setData] = useState<AccountingGrantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 篩選狀態
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null,
  );
  const [promoCode, setPromoCode] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [source, setSource] = useState("all");
  // 檢視範圍改由全域 Header（useViewScope）驅動。
  const { scope: brandScope } = useViewScope();

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (dateRange) {
      params.set("dateFrom", dateRange[0].startOf("day").toISOString());
      params.set("dateTo", dateRange[1].endOf("day").toISOString());
    }
    if (promoCode.trim()) params.set("promo_code", promoCode.trim());
    if (deviceId.trim()) params.set("device_id", deviceId.trim());
    if (userId.trim()) params.set("user_id", userId.trim());
    if (status) params.set("status", status);
    if (source !== "all") params.set("source", source);
    if (isAllBrand) params.set("brand", brandScope);
    return params;
  }, [
    page,
    pageSize,
    dateRange,
    promoCode,
    deviceId,
    userId,
    status,
    source,
    isAllBrand,
    brandScope,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_PATH}?${buildParams().toString()}`);
      const result = await res.json();
      const body = unwrap<AccountingGrantsResponse>(result);
      if (body) {
        setData(Array.isArray(body.rows) ? body.rows : []);
        setTotal(Number(body.total) || 0);
      } else {
        message.error(result?.msg || "載入發放紀錄失敗");
      }
    } catch {
      message.error("載入發放紀錄失敗");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 檢視範圍變更時回到第一頁。
  useEffect(() => {
    setPage(1);
  }, [brandScope]);

  const handleQuery = () => {
    setPage(1);
    // page 改變會觸發 fetchData；若已是 1 也需手動刷新
    fetchData();
  };

  const handleReset = () => {
    setDateRange(null);
    setPromoCode("");
    setDeviceId("");
    setUserId("");
    setStatus(undefined);
    setSource("all");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      params.delete("page");
      params.delete("pageSize");
      await downloadAccountingCsv(API_PATH, params, "accounting-grants");
      message.success("已開始下載 CSV");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "匯出失敗");
    } finally {
      setExporting(false);
    }
  };

  const columns: NonNullable<TableProps<AccountingGrantRow>["columns"]> = [
    {
      dataIndex: "created_at",
      title: "發放時間",
      width: 160,
      render: (v: string) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      dataIndex: "promo_code",
      title: "兌換碼",
      width: 150,
      render: (v: string | null) =>
        v ? (
          <Typography.Text
            copyable={{ text: v }}
            style={{ fontFamily: "monospace" }}
          >
            {v}
          </Typography.Text>
        ) : (
          <Tag>後台發放</Tag>
        ),
    },
    {
      dataIndex: "device_id",
      title: "裝置",
      width: 190,
      render: (id: string) =>
        id ? (
          <Typography.Text
            copyable={{ text: id }}
            ellipsis={{ tooltip: id }}
            style={{ maxWidth: 160, fontSize: 12, fontFamily: "monospace" }}
          >
            {id}
          </Typography.Text>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        ),
    },
    {
      dataIndex: "source",
      title: "帳本來源",
      width: 110,
      render: (value: AccountingGrantRow["source"]) => (
        <Tag color={value === "member_entitlement" ? "purple" : "default"}>
          {value === "member_entitlement" ? "會員權益" : "舊版發放"}
        </Tag>
      ),
    },
    {
      dataIndex: "member_account_id",
      title: "會員帳戶 ID",
      width: 135,
      render: (memberAccountId: AccountingGrantRow["member_account_id"]) =>
        memberAccountId != null && memberAccountId !== "" ? (
          <Link
            to={`/users?memberAccountId=${encodeURIComponent(String(memberAccountId))}`}
            aria-label={`查看會員帳戶 ID ${memberAccountId}`}
          >
            {String(memberAccountId)}
          </Link>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        ),
    },
    {
      dataIndex: "user_id",
      title: "用戶 ID",
      width: 105,
      render: (clientId: AccountingGrantRow["user_id"]) =>
        clientId != null && clientId !== "" ? String(clientId) : "—",
    },
    {
      dataIndex: "brand",
      title: "品牌",
      width: 90,
      render: (brand: string) => <Tag color="blue">{getBrandLabel(brand)}</Tag>,
    },
    {
      dataIndex: "granted_mb",
      title: "發放量",
      width: 110,
      align: "right",
      render: (v: number) => formatMb(v),
    },
    {
      dataIndex: "used_mb",
      title: "已使用",
      width: 110,
      align: "right",
      render: (v: number) => formatMb(v),
    },
    {
      dataIndex: "remaining_mb",
      title: "剩餘",
      width: 110,
      align: "right",
      render: (v: number) => formatMb(v),
    },
    {
      dataIndex: "valid_until",
      title: "有效期至",
      width: 160,
      render: (v: string | null) =>
        v ? (
          dayjs(v).format("YYYY-MM-DD HH:mm")
        ) : (
          <span style={{ color: "#999" }}>—</span>
        ),
    },
    {
      dataIndex: "status",
      title: "狀態",
      width: 100,
      render: (s: string, row) => {
        if (row.expiration_reason === "MEMBERSHIP_ISSUED") return <Tag color="default" title="會員資格發放或購買成功，原兌換碼未用餘額已過期；保留兌換與用量歷史">會員發放後過期</Tag>;
        const info = describeGrantStatus(s);
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
  ];

  return (
    <div className="page-enter">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          兌換／發放紀錄
        </Title>
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            匯出 CSV
          </Button>
        </Space>
      </div>

      <BrandScopeNotice kind="accounting" style={{ marginBottom: 16 }} />

      {/* 篩選列 */}
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
          }
          placeholder={["發放起日", "發放迄日"]}
        />
        <Input
          placeholder="兌換碼"
          allowClear
          style={{ width: 150 }}
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Input
          placeholder="裝置短碼"
          allowClear
          style={{ width: 170 }}
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Input
          placeholder="用戶 ID"
          allowClear
          style={{ width: 130 }}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Select
          placeholder="狀態"
          allowClear
          style={{ width: 120 }}
          value={status}
          onChange={(v) => setStatus(v)}
          options={STATUS_OPTIONS}
        />
        <Select
          style={{ width: 130 }}
          value={source}
          options={SOURCE_OPTIONS}
          onChange={setSource}
        />
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleQuery}
          loading={loading}
        >
          查詢
        </Button>
        <Button onClick={handleReset}>重設</Button>
      </Space>

      <Table
        rowKey={(row) => `${row.source}|${row.id}`}
        dataSource={data}
        loading={loading}
        columns={columns}
        scroll={{ x: 1510 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default AccountingGrantsPage;
